/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import CodeBlock from '@/components/code/CodeBlock';
import { APP_ROUTES } from '@/config/appRoutes';
import { useI18n } from '@/context/I18nContext';
import { NavLink } from 'react-router';

interface ApiReferenceTabProps {
    theme: 'light' | 'dark';
}

interface ApiParameter {
    description: string;
    name: string;
    required: boolean;
    type: string;
}

interface ApiOperation {
    code: string;
    demoRoute: string;
    description: string;
    endpoints: string[];
    parameters: ApiParameter[];
    response: string;
    responseDescription: string;
    title: string;
    usage: string;
}

const SHARED_RESULT_CODE = `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';

const { accessToken } = useSession();
const { data, meta } = await eeCheckApi.requestCurrentValues(
    accessToken,
    { urn: 'GN.Z04.W_1d' },
);

console.log(data);        // Endpoint-specific response data
console.log(meta.url);    // URL used by the current runtime mode
console.log(meta.method); // HTTP method
console.log(meta.status); // HTTP response status`;

const API_OPERATIONS: ApiOperation[] = [
    {
        title: 'Login and token renewal',
        demoRoute: APP_ROUTES.logindemo,
        description: 'Acquires or renews the API bearer token used by all authenticated requests.',
        endpoints: ['POST /api/token-login'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';

const { data, meta } = await eeCheckApi.login({
    username: 'your-user',
    password: 'your-password',
});

const accessToken = data.token;`,
        parameters: [
            {
                name: 'credentials',
                type: 'ApiTokenCredentials',
                required: false,
                description: 'Optional object with username and password. Without it, configured default credentials are used.',
            },
        ],
        response: `{
    data: {
        token: '71db2a1428e7067eccd11ff64dac22b037b88f4d45d69c8442dd783c06ca3606',
    },
    meta: {
        method: 'POST',
        status: 200,
        url: '/api/token-login',
        rawResponse: '71db2a1428e7067eccd11ff64dac22b037b88f4d45d69c8442dd783c06ca3606',
    },
}`,
        responseDescription: 'Store data.token and pass it to all authenticated data requests.',
        usage: 'Use this for the login form and for renewing an expired API token.',
    },
    {
        title: 'Stored Plotly data',
        demoRoute: APP_ROUTES.plotly,
        description: 'Loads a saved Plotly figure. The returned data can be passed directly to the shared Plotly components.',
        endpoints: ['POST /api/meter_charts/stored_plotly_by_tags'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';

const { accessToken } = useSession();
const { data } = await eeCheckApi.requestStoredPlotly(accessToken, {
    reference: 'base_load',
    tags: {
        meter: 'GN.Z01',
    },
    layout: null,
});`,
        parameters: [
            {
                name: 'accessToken',
                type: 'string',
                required: true,
                description: 'Current API bearer token from useSession().',
            },
            {
                name: 'payload.reference',
                type: 'string',
                required: true,
                description: 'Configured reference of the stored Plotly figure.',
            },
            {
                name: 'payload.tags',
                type: 'Record<string, string>',
                required: true,
                description: 'Tags used to resolve the requested figure, for example meter.',
            },
            {
                name: 'payload.layout',
                type: 'Record<string, unknown> | null',
                required: false,
                description: 'Optional layout overrides for the stored figure.',
            },
            {
                name: 'onProgress',
                type: '(progress) => void',
                required: false,
                description: 'Optional progress callback with currentRequest and totalRequests.',
            },
        ],
        response: `{
    data: {
        data: [{ type: 'scatter', x: ['2026-07-28'], y: [42] }],
        layout: { title: 'Base load' },
        config: { responsive: true },
    },
    meta: { method: 'POST', status: 200, url: '...' },
}`,
        responseDescription: 'Use data.data, data.layout, and data.config as Plotly figure properties.',
        usage: 'Use this for saved server-side Plotly charts, for example through Api2StoredPlotlyPlot.',
    },
    {
        title: 'Time Series',
        demoRoute: APP_ROUTES.timeseries,
        description: 'Loads CSV time series data for one or more comma-separated URNs. Large URN lists are split and merged automatically.',
        endpoints: ['GET /api/data/series/csv/timestamp/{from}/{to}/changeofvalue/{urn}'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';
import { parseTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';

const { accessToken } = useSession();
const { data } = await eeCheckApi.requestTimeSeries(accessToken, {
    from: 'NOW-4WEEKS',
    to: 'NOW',
    urn: 'GN.Z04.W_1d,GN.Z13.W_1d',
});

if (typeof data === 'string') {
    const parsedTimeSeries = parseTimeSeriesCsv(data);
    console.log(parsedTimeSeries.columns);
    console.log(parsedTimeSeries.rows);
}`,
        parameters: [
            {
                name: 'accessToken',
                type: 'string',
                required: true,
                description: 'Current API bearer token from useSession().',
            },
            {
                name: 'payload.from',
                type: 'string',
                required: true,
                description: 'Start time, for example NOW-4WEEKS or a formatted date-time value.',
            },
            {
                name: 'payload.to',
                type: 'string',
                required: true,
                description: 'End time, for example NOW or a formatted date-time value.',
            },
            {
                name: 'payload.urn',
                type: 'string',
                required: true,
                description: 'One URN or multiple comma-separated URNs. EE-Check function URNs are encoded by the client.',
            },
            {
                name: 'onProgress',
                type: '(progress) => void',
                required: false,
                description: 'Optional progress callback, especially useful when a large URN list is split.',
            },
        ],
        response: `{
    data: 'local server time;GN.Z04.W_1d\\n2026-07-28 12:00:00;2797589',
    meta: { method: 'GET', status: 200, url: '...' },
}`,
        responseDescription: 'The data field contains CSV text. Parse it with parseTimeSeriesCsv() before rendering or aggregating values.',
        usage: 'Use this for plots, aggregations, and KPI values based on a period of measurements.',
    },
    {
        title: 'Current values',
        demoRoute: APP_ROUTES.currentvalue,
        description: 'Loads the current value, or the last value before an optional timestamp, for one or more comma-separated URNs.',
        endpoints: ['POST /api/data/current_values/csv/timestamp'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';
import { parseLastValueCsv } from '@/lib/timeSeries/parseLastValueCsv';

const { accessToken } = useSession();
const { data } = await eeCheckApi.requestCurrentValues(accessToken, {
    urn: 'GN.Z04.W_1d,GN.Z13.W_1d',
    timestamp: 'NOW',
});

if (typeof data === 'string') {
    const parsedLastValues = parseLastValueCsv(data);
    console.log(parsedLastValues.rows);
}`,
        parameters: [
            {
                name: 'accessToken',
                type: 'string',
                required: true,
                description: 'Current API bearer token from useSession().',
            },
            {
                name: 'payload.urn',
                type: 'string',
                required: true,
                description: 'One URN or multiple comma-separated URNs.',
            },
            {
                name: 'payload.timestamp',
                type: 'string',
                required: false,
                description: 'Optional reference time. Omit it for the current value; examples include NOW and 2026-01-01 00:00:00.',
            },
            {
                name: 'onProgress',
                type: '(progress) => void',
                required: false,
                description: 'Optional progress callback with currentRequest and totalRequests.',
            },
        ],
        response: `{
    data: 'timestamp;urn;value;unit\\n1783325761000;GN.Z04.W_1d;2797589;kWh',
    meta: { method: 'POST', status: 200, url: '...' },
}`,
        responseDescription: 'The data field contains CSV text with timestamp, URN, value, and unit. Parse it with parseLastValueCsv().',
        usage: 'Use this for current KPI values and lightweight overview data without loading a full time series.',
    },
    {
        title: 'Interval values',
        demoRoute: APP_ROUTES.intervalvalues,
        description: 'Loads interval differences for one or more comma-separated URNs between two required timestamps.',
        endpoints: ['GET /api/data/interval_diff/csv/timestamp/{from}/{to}/{interval}/{urn}'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';
import { parseIntervalValueCsv } from '@/lib/timeSeries/parseIntervalValueCsv';

const { accessToken } = useSession();
const { data } = await eeCheckApi.requestIntervalValues(accessToken, {
    from: '2018-01-01',
    to: 'NOW',
    interval: '1year',
    urn: 'GN.Z04.W_1d,GN.Z13.W_1d',
});

if (typeof data === 'string') {
    const parsedIntervalValues = parseIntervalValueCsv(data);
    console.log(parsedIntervalValues.series);
    console.log(parsedIntervalValues.rows);
}`,
        parameters: [
            {
                name: 'accessToken',
                type: 'string',
                required: true,
                description: 'Current API bearer token from useSession().',
            },
            {
                name: 'payload.from',
                type: 'string',
                required: true,
                description: 'Required start time, for example 2018-01-01.',
            },
            {
                name: 'payload.to',
                type: 'string',
                required: true,
                description: 'Required end time, for example NOW or a formatted date-time value.',
            },
            {
                name: 'payload.interval',
                type: 'string',
                required: false,
                description: 'Optional interval. The default is 1year; another example is 1month.',
            },
            {
                name: 'payload.urn',
                type: 'string',
                required: true,
                description: 'One URN or multiple comma-separated URNs.',
            },
            {
                name: 'onProgress',
                type: '(progress) => void',
                required: false,
                description: 'Optional progress callback with currentRequest and totalRequests.',
            },
        ],
        response: `{
    data: 'local server time;timestamp;;GN.Z04.W_1d [kWh];GN.Z13.W_1d [kWh]\\n2026-01-01 00:00:00;1767222000;;329661.37;18479.94',
    meta: { method: 'GET', status: 200, url: '...' },
}`,
        responseDescription: 'The data field contains CSV text. parseIntervalValueCsv() extracts timestamps and sensor columns together with their units.',
        usage: 'Use this for interval-based consumption values without calculating differences from meter readings in the application.',
    },
    {
        title: 'Chart Definitions',
        demoRoute: APP_ROUTES.chartdefinitions,
        description: 'Loads a stored chart definition by name. Chart.js responses are converted before rendering, while Plotly responses are used directly.',
        endpoints: ['GET /api/meter_charts/stored_chartjs_by_definition_name?definition_name={definitionName}&ignore_class_check=1'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';
import { ChartConfigDetector } from '@/lib/conversion/ChartConfigDetector';
import { ChartJsToPlotlyConverter } from '@/lib/conversion/ChartJsToPlotlyConverter';

const { accessToken } = useSession();
const { data } = await eeCheckApi.requestStoredChartJs(accessToken, {
    definitionName: 'nt_heat_demand_map_3y',
});

const plotlyFigure = ChartConfigDetector.detect(data) === 'chartjs'
    ? new ChartJsToPlotlyConverter().convert(data)
    : data;`,
        parameters: [
            {
                name: 'accessToken',
                type: 'string',
                required: true,
                description: 'Current API bearer token from useSession().',
            },
            {
                name: 'payload.definitionName',
                type: 'string',
                required: true,
                description: 'Name of the stored chart definition to load.',
            },
            {
                name: 'ignore_class_check',
                type: '1',
                required: true,
                description: 'Fixed query parameter added automatically by the client.',
            },
        ],
        response: `{
    data: {
        type: 'bar',
        data: {
            labels: ['2024', '2025'],
            datasets: [{ label: 'Heat demand', data: [42, 38] }],
        },
        options: { scales: { x: { stacked: true } } },
    },
    meta: { method: 'GET', status: 200, url: '...' },
}`,
        responseDescription: 'The data field contains Chart.js or Plotly JSON. Convert only Chart.js configurations with ChartJsToPlotlyConverter before using shared Plotly components.',
        usage: 'Use this for server-stored chart definitions that should be rendered with the shared Plotly chart components.',
    },
    {
        title: 'Building statistics',
        demoRoute: APP_ROUTES.buildingstatistics,
        description: 'Loads daily, monthly, and yearly energy statistics for every available supply type of a building.',
        endpoints: ['GET /api/data/building_statistics/{system}'],
        code: `import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';

const { accessToken } = useSession();
const { data } = await eeCheckApi.requestBuildingStatistics(
    accessToken,
    'Mue.0531',
);

if (data.success && data.data && !Array.isArray(data.data.pv)) {
    console.log(data.data.pv.daily_W.values);
    console.log(data.data.pv.daily_W.timestamps);
}

// The Neutraubling UI label GN1 is requested as GN.Building.`,
        parameters: [
            {
                name: 'accessToken',
                type: 'string',
                required: true,
                description: 'Current API bearer token from useSession().',
            },
            {
                name: 'system',
                type: 'string',
                required: true,
                description: 'Building system URN, for example Mue.0531 or GN.Building.',
            },
        ],
        response: `{
    data: {
        success: true,
        error: false,
        timestamp: 1786439602000,
        data: {
            building: { urn: 'Mue.0531' },
            pv: {
                urn: 'Mue.0531.pP_00011911.W',
                daily_W: {
                    urn: 'RET(Mue.0531.pP_00011911_V.W/unit=kWh)',
                    unit: 'kWh',
                    values: [50.48, 52.17],
                    timestamps: [1546257600, 1786449600],
                },
                yearly_W: { unit: 'kWh', values: [], timestamps: [] },
                monthly_W: { unit: 'kWh', values: [], timestamps: [] },
                daily_W_Abs: { unit: 'kWh', values: [], timestamps: [] },
            },
            gas: [],
            heat: [],
            water: [],
            grid_feed: [],
            grid_supply: [],
        },
    },
    meta: { method: 'GET', status: 200, url: '...' },
}`,
        responseDescription: 'Each supply type is either an object with four aligned value and timestamp series or an empty array when no data is available.',
        usage: 'Use this for compact building consumption summaries without requesting and calculating every energy interval separately.',
    },
];

const CONFIGURATION_PARAMETERS: ApiParameter[] = [
    {
        name: 'accessToken',
        type: 'string',
        required: true,
        description: 'Current API bearer token from useSession().',
    },
    {
        name: 'tags.customer',
        type: "'neutraubling' | 'muenster'",
        required: true,
        description: 'Customer used to identify the configuration.',
    },
    {
        name: 'tags.system',
        type: 'string',
        required: true,
        description: 'System used to identify the configuration.',
    },
    {
        name: 'content',
        type: 'string',
        required: true,
        description: 'Valid JSON string to save. Only required for saveConfiguration().',
    },
    {
        name: 'data_type',
        type: "'array'",
        required: true,
        description: 'Always sent as array by saveConfiguration().',
    },
];

interface ApiOperationReferenceProps {
    number: number;
    operation: ApiOperation;
    theme: 'light' | 'dark';
}

function ApiOperationReference({ number, operation, theme }: ApiOperationReferenceProps) {
    const { t } = useI18n();

    return (
        <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                    <span className="badge badge-primary self-start">API</span>
                    <h3>{`${number}. ${t(operation.title)}`}</h3>
                </div>
                <p>{t(operation.description)}</p>
                <div className="text-sm text-base-content/75">
                    <span className="font-semibold">{t('Original endpoint')}:</span>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                        {operation.endpoints.map((endpoint) => (
                            <li key={endpoint}>
                                <code className="rounded bg-base-200 px-1.5 py-0.5 font-mono text-xs">
                                    {endpoint}
                                </code>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <NavLink className="btn btn-outline btn-sm" to={operation.demoRoute}>
                        {t('Open demo')}
                    </NavLink>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <h4>{t('Call example')}</h4>
                <CodeBlock code={operation.code} theme={theme} />
            </div>

            <div className="flex flex-col gap-2">
                <h4>{t('Parameters')}</h4>
                <div className="overflow-x-auto rounded-box border border-base-content/20">
                    <table className="table table-sm w-full">
                        <thead className="bg-base-content/10 text-sm uppercase tracking-wide text-base-content/80">
                            <tr>
                                <th className="px-4 py-3">{t('Parameter')}</th>
                                <th className="px-4 py-3">{t('Type')}</th>
                                <th className="px-4 py-3">{t('Required')}</th>
                                <th className="px-4 py-3">{t('Description')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-base-300">
                            {operation.parameters.map((parameter) => (
                                <tr key={parameter.name} className="even:bg-base-content/5 hover:bg-base-content/10">
                                    <td className="align-top px-4 py-3">
                                        <code className="whitespace-nowrap rounded bg-base-200 px-1.5 py-0.5 font-mono text-sm">
                                            {parameter.name}
                                        </code>
                                    </td>
                                    <td className="align-top px-4 py-3 font-mono text-sm text-base-content/80">
                                        {parameter.type}
                                    </td>
                                    <td className="align-top px-4 py-3">
                                        <span className={`badge ${parameter.required ? 'badge-primary' : 'badge-ghost'}`}>
                                            {parameter.required ? t('Yes') : t('No')}
                                        </span>
                                    </td>
                                    <td className="min-w-80 align-top px-4 py-3">
                                        {t(parameter.description)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <h4>{t('Example response')}</h4>
                <CodeBlock code={operation.response} language="json" theme={theme} />
                <p className="text-sm text-base-content/75">{t(operation.responseDescription)}</p>
            </div>

            <div className="rounded-box border border-base-content/15 bg-base-200/70 px-4 py-3 text-sm text-base-content/85">
                <span className="font-semibold">{t('Use case')}:</span> {t(operation.usage)}
            </div>
        </section>
    );
}

function ApiReferenceTab({ theme }: ApiReferenceTabProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-10" aria-label={t('API')}>
            <section className="flex flex-col gap-2">
                <h2>{t('API')}</h2>
                <p>{t('Reference for calling the central EE-Check API client from project components.')}</p>
            </section>

            <section className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                    <span className="badge badge-info self-start">{t('Foundation')}</span>
                    <h3>{t('Shared response format')}</h3>
                </div>
                <p>{t('Every API operation returns data together with request metadata. The client selects the appropriate transport automatically for developer and live builds.')}</p>
                <CodeBlock code={SHARED_RESULT_CODE} theme={theme} />
            </section>

            <ApiOperationReference number={1} operation={API_OPERATIONS[0]!} theme={theme} />

            <section className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                        <span className="badge badge-primary relative -top-1">API</span>
                        <h3>2. {t('Configuration')}</h3>
                    </div>
                    <p>{t('Read and save arbitrary JSON configuration data for a customer and system. Configuration reads and writes use JSON requests and are not cached.')}</p>
                    <div className="text-sm text-base-content/75">
                        <span className="font-semibold">{t('Original endpoints')}:</span>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                            <li><code className="rounded bg-base-200 px-1.5 py-0.5 font-mono text-xs">POST /api/data/get_file_by_tags</code></li>
                            <li><code className="rounded bg-base-200 px-1.5 py-0.5 font-mono text-xs">POST /api/data/upload_file_by_tags</code></li>
                        </ul>
                    </div>
                    <div>
                        <NavLink className="btn btn-outline btn-sm" to={APP_ROUTES.configuration}>
                            {t('Open demo')}
                        </NavLink>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <h4>{t('Read configuration')}</h4>
                    <CodeBlock
                        code={`import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';

const { accessToken } = useSession();
const { data } = await eeCheckApi.readConfiguration(accessToken, {
    customer: 'neutraubling',
    system: 'GN1',
});

const configuration = data.success ? data.data?.data : {};`}
                        theme={theme}
                    />
                    <p className="text-sm text-base-content/75">{t('The tags object requires customer and system. A missing configuration returns success: false and can be handled as an empty object.')}</p>
                </div>

                <div className="flex flex-col gap-2">
                    <h4>{t('Save configuration')}</h4>
                    <CodeBlock
                        code={`import { eeCheckApi } from '@/api/eeCheckApi';
import { useSession } from '@/context/SessionContext';

const { accessToken } = useSession();
const configuration = { int: 123, string: 'abc' };
const { data } = await eeCheckApi.saveConfiguration(
    accessToken,
    { customer: 'neutraubling', system: 'GN1' },
    JSON.stringify(configuration),
);

console.log(data.success); // true`}
                        theme={theme}
                    />
                    <p className="text-sm text-base-content/75">{t('The client always sends data_type: array and stores the supplied JSON string as configuration content.')}</p>
                </div>

                <div className="flex flex-col gap-2">
                    <h4>{t('Parameters')}</h4>
                    <div className="overflow-x-auto rounded-box border border-base-content/20">
                        <table className="table table-sm w-full">
                            <thead className="bg-base-content/10 text-sm uppercase tracking-wide text-base-content/80">
                                <tr>
                                    <th className="px-4 py-3">{t('Parameter')}</th>
                                    <th className="px-4 py-3">{t('Type')}</th>
                                    <th className="px-4 py-3">{t('Required')}</th>
                                    <th className="px-4 py-3">{t('Description')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-base-300">
                                {CONFIGURATION_PARAMETERS.map((parameter) => (
                                    <tr key={parameter.name} className="even:bg-base-content/5 hover:bg-base-content/10">
                                        <td className="align-top px-4 py-3"><code className="whitespace-nowrap rounded bg-base-200 px-1.5 py-0.5 font-mono text-sm">{parameter.name}</code></td>
                                        <td className="align-top px-4 py-3 font-mono text-sm text-base-content/80">{parameter.type}</td>
                                        <td className="align-top px-4 py-3"><span className={`badge ${parameter.required ? 'badge-primary' : 'badge-ghost'}`}>{parameter.required ? t('Yes') : t('No')}</span></td>
                                        <td className="min-w-80 align-top px-4 py-3">{t(parameter.description)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <h4>{t('Example response')}</h4>
                    <CodeBlock
                        language="json"
                        code={`// readConfiguration()
{
    "success": true,
    "error": false,
    "data": {
        "data": { "int": 123, "string": "abc" }
    }
}

// saveConfiguration()
{
    "success": true,
    "error": false,
    "message": "inserted"
}`}
                        theme={theme}
                    />
                    <p className="text-sm text-base-content/75">{t('Read the saved JSON through data.data. When no configuration exists, data is null and success is false.')}</p>
                </div>
            </section>

            {[
                APP_ROUTES.timeseries,
                APP_ROUTES.currentvalue,
                APP_ROUTES.intervalvalues,
                APP_ROUTES.plotly,
                APP_ROUTES.chartdefinitions,
                APP_ROUTES.buildingstatistics,
            ].map((demoRoute, index) => {
                const operation = API_OPERATIONS.find((candidate) => candidate.demoRoute === demoRoute);

                return operation ? (
                    <ApiOperationReference
                        key={operation.title}
                        number={index + 3}
                        operation={operation}
                        theme={theme}
                    />
                ) : null;
            })}
        </div>
    );
}

export default ApiReferenceTab;
